/**
 * Public, magic-link patient view of a prescribed HEP. No auth.
 *
 * Design goals (May 2026 redesign):
 *   - Mobile-first hero: practice brand + provider greeting + progress
 *     visualization (last 14 days)
 *   - Per-exercise card: big visual on top (image or region-colored
 *     gradient placeholder with body silhouette), then dosing chips
 *     emphasized in brand-red, then instructions, then provider notes
 *   - Done-today CTA is the only thing competing with the visual hero
 *   - Compliance calendar at top (green dots = days you logged anything)
 *   - "Download PDF" + "Print" actions in the header
 *   - Region-color-coded badges (cervical=indigo, thoracic=purple, etc.)
 *     match the schedule's AppointmentBlock coloring
 *
 * The point: when the patient lands on this page from email, they
 * should feel like they got something professional and care-grade —
 * not a freebie WordPress plugin printout.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Activity, Printer, AlertTriangle, Clock, Target, Wrench,
  CheckCircle2, Circle, Download, Calendar, Play, Flame,
} from 'lucide-react';
import api from '@/services/api';

// Region color tokens — match the SchedulePage's AppointmentBlock palette
// so patient-facing visuals feel consistent with what the practice sees
// internally. When an exercise has no image, the card uses this region's
// gradient as the hero so the patient still gets visual orientation.
const REGION_PALETTE = {
  cervical:   { bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100', accent: 'bg-indigo-500', text: 'text-indigo-700', ring: 'ring-indigo-200', label: 'Neck' },
  thoracic:   { bg: 'bg-gradient-to-br from-purple-50 to-purple-100', accent: 'bg-purple-500', text: 'text-purple-700', ring: 'ring-purple-200', label: 'Mid Back' },
  lumbar:     { bg: 'bg-gradient-to-br from-rose-50 to-rose-100',     accent: 'bg-rose-500',   text: 'text-rose-700',   ring: 'ring-rose-200',   label: 'Low Back' },
  sacroiliac: { bg: 'bg-gradient-to-br from-amber-50 to-amber-100',   accent: 'bg-amber-500',  text: 'text-amber-700',  ring: 'ring-amber-200',  label: 'SI Joint' },
  hip:        { bg: 'bg-gradient-to-br from-orange-50 to-orange-100', accent: 'bg-orange-500', text: 'text-orange-700', ring: 'ring-orange-200', label: 'Hip' },
  shoulder:   { bg: 'bg-gradient-to-br from-teal-50 to-teal-100',     accent: 'bg-teal-500',   text: 'text-teal-700',   ring: 'ring-teal-200',   label: 'Shoulder' },
  knee:       { bg: 'bg-gradient-to-br from-cyan-50 to-cyan-100',     accent: 'bg-cyan-500',   text: 'text-cyan-700',   ring: 'ring-cyan-200',   label: 'Knee' },
  ankle:      { bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100', accent: 'bg-emerald-500', text: 'text-emerald-700', ring: 'ring-emerald-200', label: 'Ankle' },
  wrist:      { bg: 'bg-gradient-to-br from-sky-50 to-sky-100',       accent: 'bg-sky-500',    text: 'text-sky-700',    ring: 'ring-sky-200',    label: 'Wrist' },
  core:       { bg: 'bg-gradient-to-br from-violet-50 to-violet-100', accent: 'bg-violet-500', text: 'text-violet-700', ring: 'ring-violet-200', label: 'Core' },
  full_body:  { bg: 'bg-gradient-to-br from-slate-50 to-slate-100',   accent: 'bg-slate-500',  text: 'text-slate-700',  ring: 'ring-slate-200',  label: 'Full Body' },
};
function regionPalette(regions) {
  const first = (regions || [])[0];
  return REGION_PALETTE[first] || REGION_PALETTE.full_body;
}

export default function HepViewPage() {
  const { token } = useParams();
  // A new magic link must never inherit another recipient's data or signature.
  return <HepViewSession key={token} token={token} />;
}

function HepViewSession({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/hep-view/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'Could not load this program.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <FullPageStatus><p className="text-gray-500">Loading your program…</p></FullPageStatus>;
  if (error) {
    return (
      <FullPageStatus>
        <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
        <p className="text-gray-900 font-semibold">{error}</p>
        <p className="text-gray-500 text-sm mt-2">If you believe this is a mistake, please call our office.</p>
      </FullPageStatus>
    );
  }

  const { program, patient, provider, practice_name, items, compliance } = data;
  const completedCount = items.filter((it) => it.completed_today).length;
  const totalCount = items.length;
  const pctToday = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  function pdfUrl() {
    return `/api/hep-view/${token}/pdf`;
  }
  async function downloadPdf() {
    try {
      const res = await api.get(`/hep-view/${token}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `home-exercise-program.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      // Fall back to opening the route directly
      window.open(pdfUrl(), '_blank');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 print:bg-white print:pb-0">
      {/* Header band */}
      <header className="bg-[#CC0000] text-white print:bg-white print:text-black">
        <div className="max-w-3xl mx-auto px-5 py-6 print:py-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 print:opacity-100 print:text-gray-600">{practice_name}</p>
              <h1 className="mt-1 text-[26px] font-bold leading-tight">Your Home Exercise Program</h1>
              <p className="mt-1.5 text-[13px] opacity-95 print:text-gray-700">
                Hi {patient?.first_name || 'there'} — prescribed by {provider?.name || 'your provider'} on {formatDate(program.prescribed_at)}
              </p>
            </div>
            <div className="flex gap-2 print:hidden">
              <button onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-md text-[12px] font-semibold transition">
                <Printer className="h-3.5 w-3.5" /> Print
              </button>
              <button onClick={downloadPdf}
                className="inline-flex items-center gap-1.5 bg-white text-[#CC0000] hover:bg-rose-50 px-3 py-1.5 rounded-md text-[12px] font-semibold transition">
                <Download className="h-3.5 w-3.5" /> Save PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5 print:px-0">
        {/* Progress + compliance calendar */}
        <ProgressPanel
          pctToday={pctToday}
          completedCount={completedCount}
          totalCount={totalCount}
          activeDays={compliance?.active_days || []}
          windowDays={compliance?.window_days || 14} />

        {/* Provider notes to patient */}
        {program.notes_to_patient && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 print:bg-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#CC0000]">A note from your provider</p>
            <p className="mt-2 text-[14px] text-gray-800 whitespace-pre-wrap leading-relaxed">{program.notes_to_patient}</p>
          </div>
        )}

        {/* Safety disclaimer */}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 flex gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-amber-900 leading-relaxed">
            <span className="font-semibold">Important: </span>
            Stop any exercise that increases pain. Mild discomfort that resolves within 24 hours is acceptable; sharp pain, numbness, or weakness is not. Contact our office before continuing.
          </div>
        </div>

        {/* Exercises */}
        <div className="space-y-4">
          {items.map((it, i) => (
            <ExerciseCard
              key={it.id}
              item={it}
              index={i + 1}
              token={token}
              onToggled={(completedToday) => {
                setData((d) => ({
                  ...d,
                  items: d.items.map((x) => x.id === it.id ? { ...x, completed_today: completedToday } : x),
                }));
              }} />
          ))}
        </div>

        {/* Footer */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 print:hidden text-[12px] text-gray-600">
          Questions? Contact our office or reply to the email this came from.
        </div>
      </main>
    </div>
  );
}

// ===========================================================================
// Progress + compliance calendar
// ===========================================================================
function ProgressPanel({ pctToday, completedCount, totalCount, activeDays, windowDays }) {
  // Build a list of the last N days, oldest → newest, with a flag for
  // each that says whether the patient logged anything that day.
  const days = useMemo(() => {
    const out = [];
    const active = new Set(activeDays);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = windowDays - 1; i >= 0; i--) {
      const d = new Date(today.getTime() - i * 86400000);
      const iso = d.toISOString().slice(0, 10);
      out.push({ date: d, iso, active: active.has(iso), isToday: i === 0 });
    }
    return out;
  }, [activeDays, windowDays]);

  // Streak — count contiguous active days back from today.
  const streak = useMemo(() => {
    let s = 0;
    for (let i = days.length - 1; i >= 0; i--) {
      if (days[i].active) s++;
      else break;
    }
    return s;
  }, [days]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 print:hidden">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">Today</p>
          <p className="mt-1 text-[20px] font-bold text-gray-900">
            {completedCount}/{totalCount} <span className="font-medium text-gray-500 text-[13px]">exercises done</span>
          </p>
        </div>
        {streak > 0 && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 ring-1 ring-amber-200">
            <Flame className="h-3.5 w-3.5 text-amber-600" />
            <p className="text-[11px] font-bold text-amber-700">{streak}-day streak</p>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-300"
          style={{ width: `${pctToday}%` }} />
      </div>

      {/* Calendar dots */}
      <div className="mt-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-1.5 flex items-center gap-1.5">
          <Calendar className="h-3 w-3" /> Last {windowDays} days
        </p>
        <div className="flex gap-1 justify-between">
          {days.map((d) => (
            <div key={d.iso}
              title={`${d.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${d.active ? ' — logged' : ''}`}
              className={`flex-1 max-w-[24px] h-7 rounded-md flex items-end justify-center text-[8px] font-bold ${
                d.active
                  ? 'bg-gradient-to-b from-emerald-400 to-emerald-500 text-white'
                  : d.isToday
                    ? 'bg-gray-100 ring-2 ring-[#CC0000]/40 text-gray-500'
                    : 'bg-gray-100 text-gray-400'
              }`}>
              {d.date.getDate()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Exercise card
// ===========================================================================
function ExerciseCard({ item, index, token, onToggled }) {
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const actionLock = useRef(false);
  const [showRating, setShowRating] = useState(false);
  const [pain, setPain] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const done = !!item.completed_today;
  const palette = regionPalette(item.exercise_regions);

  async function toggle() {
    if (actionLock.current) return;
    actionLock.current = true;
    setSaveError(null);
    setBusy(true);
    try {
      if (done) {
        await api.delete(`/hep-view/${token}/log/${item.id}`);
        onToggled(false);
        setShowRating(false);
      } else {
        await api.post(`/hep-view/${token}/log`, { item_id: item.id });
        onToggled(true);
        setShowRating(true);
      }
    } catch { setSaveError('Your exercise update could not be confirmed. Refresh this page to check your progress before trying again.'); } finally { actionLock.current = false; setBusy(false); }
  }

  async function saveRating() {
    if (actionLock.current) return;
    actionLock.current = true;
    setSaveError(null);
    setBusy(true);
    try {
      await api.post(`/hep-view/${token}/log`, {
        item_id: item.id, pain_after: pain, difficulty,
      });
      setShowRating(false);
    } catch { setSaveError('Your rating could not be saved. Your choices are still here; please try again.'); } finally { actionLock.current = false; setBusy(false); }
  }

  return (
    <div className={`rounded-2xl border overflow-hidden shadow-sm print:shadow-none print:break-inside-avoid transition ${
      done ? 'border-emerald-300 bg-emerald-50/30 ring-1 ring-emerald-100' : 'border-gray-200 bg-white'
    }`}>
      {saveError && <p role="alert" className="p-3 text-sm text-red-800 bg-red-50">{saveError}</p>}
      {/* Visual hero */}
      <div className={`relative ${item.image_url ? 'bg-gray-900' : palette.bg} h-44 sm:h-52 overflow-hidden`}>
        {item.image_url ? (
          <img src={item.image_url}
            alt={item.exercise_name}
            className="w-full h-full object-contain print:object-contain" />
        ) : (
          <RegionPlaceholder palette={palette} regions={item.exercise_regions || []} />
        )}
        {/* Index chip */}
        <span className="absolute top-3 left-3 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/90 backdrop-blur text-[12px] font-bold text-gray-900 print:bg-white">
          {index}
        </span>
        {/* Done chip */}
        {done && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2.5 py-1 text-[11px] font-bold print:hidden">
            <CheckCircle2 className="h-3 w-3" /> Done today
          </span>
        )}
        {/* Region pill */}
        <span className={`absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold ${palette.text}`}>
          {palette.label} · {(item.exercise_type || '').replace('_', ' ')}
        </span>
      </div>

      <div className="px-4 sm:px-5 py-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-[16px] sm:text-[17px] font-bold text-gray-900 leading-tight">{item.exercise_name}</h3>
          <button onClick={toggle} disabled={busy}
            aria-label={done ? 'Mark not done' : 'Mark done today'}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition print:hidden ${
              done
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-emerald-500 hover:text-emerald-700'
            }`}>
            {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            {done ? 'Done today' : 'Mark done'}
          </button>
        </div>

        {/* Dosing — big and obvious. The thing patients miss most. */}
        <div className="flex flex-wrap gap-2 mb-4">
          {item.sets && <DosingPill label="Sets" value={item.sets} />}
          {item.reps && item.reps > 1 && <DosingPill label="Reps" value={item.reps} />}
          {item.hold_seconds && <DosingPill icon={Clock} label="Hold" value={`${item.hold_seconds}s`} />}
          {item.duration_seconds && !item.hold_seconds && (
            <DosingPill icon={Clock} label="Duration"
              value={item.duration_seconds >= 60 ? `${Math.round(item.duration_seconds / 60)} min` : `${item.duration_seconds}s`} />
          )}
          {item.frequency && <DosingPill icon={Clock} label="Frequency" value={item.frequency} />}
        </div>

        {/* Equipment */}
        {Array.isArray(item.equipment) && item.equipment.filter((e) => e !== 'none').length > 0 && (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-[11px] text-gray-700 ring-1 ring-gray-200">
            <Wrench className="h-3 w-3" />
            Equipment: <span className="font-semibold">{item.equipment.filter((e) => e !== 'none').join(', ')}</span>
          </div>
        )}

        {/* Instructions */}
        <p className="text-[14px] text-gray-800 whitespace-pre-wrap leading-relaxed">{item.patient_instructions}</p>

        {/* Provider note */}
        {item.notes && (
          <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-[12.5px] text-blue-900">
            <span className="font-bold">Provider note: </span>{item.notes}
          </div>
        )}

        {/* Video */}
        {item.video_url && (
          <a href={item.video_url} target="_blank" rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#CC0000] text-white hover:bg-[#A30000] px-3 py-1.5 text-[12px] font-bold transition print:hidden">
            <Play className="h-3 w-3 fill-current" /> Watch demonstration
          </a>
        )}

        {/* Rating popover after marking done */}
        {showRating && done && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 print:hidden">
            <p className="text-[12px] font-bold text-gray-900">How did this go? <span className="font-normal text-gray-500">(optional)</span></p>
            <div className="mt-2.5">
              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Pain after (0 = none, 10 = worst)</p>
              <div className="flex flex-wrap gap-1">
                {[0,1,2,3,4,5,6,7,8,9,10].map((v) => (
                  <button key={v} onClick={() => setPain(v)}
                    className={`flex-1 min-w-[28px] h-8 rounded text-[11px] font-bold transition ${
                      pain === v ? 'bg-[#CC0000] text-white' : 'bg-white text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200'
                    }`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Difficulty (1 = easy, 5 = very hard)</p>
              <div className="flex gap-1">
                {[1,2,3,4,5].map((v) => (
                  <button key={v} onClick={() => setDifficulty(v)}
                    className={`flex-1 py-2 rounded text-[12px] font-bold transition ${
                      difficulty === v ? 'bg-[#CC0000] text-white' : 'bg-white text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200'
                    }`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={() => setShowRating(false)} className="text-[12px] text-gray-500 hover:text-gray-900 font-semibold">Skip</button>
              <button onClick={saveRating} disabled={busy}
                className="rounded-md bg-[#CC0000] hover:bg-[#A30000] text-white text-[12px] font-bold px-3 py-1.5">
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DosingPill({ icon: Icon, label, value }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-md bg-[#CC0000]/5 ring-1 ring-[#CC0000]/15 px-2.5 py-1.5">
      {Icon && <Icon className="h-3 w-3 text-[#CC0000]" />}
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#CC0000]/70">{label}</span>
      <span className="text-[13px] font-bold text-gray-900">{value}</span>
    </div>
  );
}

/**
 * Region-colored hero placeholder when no image_url. Renders a simple
 * SVG body silhouette with the targeted region highlighted, so the
 * patient still has something visually orienting on the card. Not
 * anatomically perfect — just enough to convey "this targets the neck"
 * vs "this targets the lower back."
 */
function RegionPlaceholder({ palette, regions }) {
  const r = (regions || [])[0] || 'full_body';
  return (
    <div className={`w-full h-full ${palette.bg} flex items-center justify-center`}>
      <svg viewBox="0 0 100 200" className="h-full max-h-44 sm:max-h-52 opacity-60" aria-hidden>
        {/* head */}
        <circle cx="50" cy="22" r="11" className={r === 'cervical' || r === 'full_body' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* cervical */}
        <rect x="46" y="33" width="8" height="10" rx="2" className={r === 'cervical' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* thoracic */}
        <rect x="38" y="43" width="24" height="34" rx="4" className={r === 'thoracic' || r === 'shoulder' || r === 'full_body' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* lumbar */}
        <rect x="40" y="77" width="20" height="22" rx="4" className={r === 'lumbar' || r === 'core' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* pelvis / hip */}
        <rect x="36" y="99" width="28" height="16" rx="5" className={r === 'sacroiliac' || r === 'hip' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* arms */}
        <rect x="22" y="48" width="14" height="42" rx="6" className={r === 'shoulder' || r === 'wrist' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        <rect x="64" y="48" width="14" height="42" rx="6" className={r === 'shoulder' || r === 'wrist' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        {/* legs */}
        <rect x="38" y="115" width="10" height="55" rx="4" className={r === 'hip' || r === 'knee' || r === 'ankle' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
        <rect x="52" y="115" width="10" height="55" rx="4" className={r === 'hip' || r === 'knee' || r === 'ankle' ? `${palette.accent.replace('bg-', 'fill-')}` : 'fill-white/50'} />
      </svg>
    </div>
  );
}

function FullPageStatus({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
      {children}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
